/**
 * ROUND4 §1: does a vehicle's run hold up? A vehicle does not path-find - it stops dead at anything solid and
 * the men aboard get out - which is a sound failure mode in the sim and a bad one on screen. So a run that a
 * prop blocks is a validation FAULT: the map is re-rolled rather than shipped with a lorry stopped in a field.
 *
 * Split out of `routes.ts` for the file limit; `checkStanding` is its only caller.
 */

import { hullBlocked } from '../../sim/enemies/specials/vehicleHull';
import { atan2 } from '../../sim/math';
import { collidersFor } from '../../sim/props';
import type { MissionDef, SpawnDef } from '../../sim/types';

/**
 * R4: props that are already gone by the time this vehicle moves, and which its run may therefore pass
 * through - the parked thing it IS (`replacesPropId`), and, when the event that wakes it is a `targetsLeave`,
 * every target of the mission (that effect removes them all in the same tick: the train steams out through
 * the sidings its own wagons stood on).
 */
export function alreadyGone(def: MissionDef, s: SpawnDef): Set<number> {
  const gone = new Set<number>();
  if (s.replacesPropId !== undefined) gone.add(s.replacesPropId);
  const tag = s.wakeTag;
  const leaves = tag !== undefined && def.objective.rules.some((r) => {
    if (r.type !== 'timedEvent' && r.type !== 'alertConsequence') return false;
    return r.effect === 'targetsLeave' && ((r.type === 'timedEvent' ? r.wakes : r.wakes) ?? r.effect) === tag;
  });
  if (leaves) for (const id of def.objective.targetPropIds) gone.add(id);
  return gone;
}

/**
 * R4: the first point of a vehicle's run where its hull would hit something solid - the whole swept capsule,
 * sampled a metre at a time along the polyline, against every prop that is not `crushable` and is still there
 * when it drives. Null = a clear run, which is what every mission with a vehicle has to offer.
 */
export function firstBlock(
  def: MissionDef, s: SpawnDef, route: ReadonlyArray<readonly [number, number]>,
): { x: number; z: number; propId: number } | null {
  if (route.length < 2) return null;
  const gone = alreadyGone(def, s);
  const solids = def.props
    .filter((p) => !gone.has(p.id))
    .flatMap((p) => collidersFor(p))
    .filter((c) => c.blocksMove && c.crushable !== true);
  // Where it stands before it pulls away, then every metre of the run.
  const legs: Array<[readonly [number, number], readonly [number, number]]> = [[[s.x, s.z], route[0] ?? [s.x, s.z]]];
  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1];
    const b = route[i];
    if (a && b) legs.push([a, b]);
  }
  for (const [a, b] of legs) {
    const span = Math.sqrt((b[0] - a[0]) * (b[0] - a[0]) + (b[1] - a[1]) * (b[1] - a[1]));
    const facing = span === 0 ? 0 : atan2(b[0] - a[0], b[1] - a[1]);
    const steps = Math.max(1, Math.ceil(span));
    for (let i = 0; i <= steps; i++) {
      const x = a[0] + ((b[0] - a[0]) * i) / steps;
      const z = a[1] + ((b[1] - a[1]) * i) / steps;
      const hit = solids.find((c) => hullBlocked([c], s.kind, x, z, facing));
      if (hit) return { x: Math.round(x), z: Math.round(z), propId: hit.propId };
    }
  }
  return null;
}
